/* Seitengeometrie der Player Card — reine Zahlen und Rechnung, kein jsPDF, kein DOM.
 *
 * Eine Stelle für Maße und Anordnung: Der Renderer (playerProfilePdf.js) zeichnet danach, die Vorschau im Reiter
 * Material (PlayerCardPreview.vue) schneidet daraus an einer natürlichen Kante ab, ohne jsPDF in das Start-Bundle zu
 * ziehen, und die Tests prüfen Raster und Rhythmus an den Zahlen.
 *
 * Maße in Millimetern, A4 hoch.
 */

export const PAGE = { W: 210, H: 297, M: 16 };
export const CONTENT_W = PAGE.W - 2 * PAGE.M;
export const FOOT_LINE = PAGE.H - 22;
export const HEAD_H = 90;

/* Raster (D8): EIN Satz Spaltenlinien für die ganze Seite. Eine breite linke Spalte (58 %) und eine schmale rechte (42 %)
   mit derselben Lücke GAP; die erste Kachelreihe hat drei gleich breite Zellen, alle weiteren Kachelreihen und der untere
   Bereich (Spielfeld | Video-Kasten) liegen auf den beiden Spalten. Alle Abstände sind Vielfache des Basismaßes BASE. */
export const BASE = 4;
export const GAP = BASE;
export const LEFT_W = (CONTENT_W - GAP) * 0.58;
export const RIGHT_W = CONTENT_W - GAP - LEFT_W;
export const COL_X = [PAGE.M, PAGE.M + LEFT_W + GAP]; // linke Kanten der beiden Spalten (mm)

/* Kacheln */
export const TILE_KEYS = ['born', 'height', 'foot', 'nationality', 'club', 'nationalTeam'];
export const TILE_GAP = GAP;
export const HEAD_TO_TILES = 4 * BASE; // Lücke 1 (fest: die Vorschau schneidet davon ab)
export const TILES_Y = HEAD_H + HEAD_TO_TILES;
export const TILE_COLS = 3;
/** Weniger als vier Kacheln: etwas höher, damit der Block nicht winzig wirkt. */
export const tileHeight = (count) => (count < 4 ? 24 : 20);

/* Unterer Bereich */
export const HEAT_RATIO = 68 / 105; // Spielfeld: Höhe / Breite
export const HEAT_EXTRA = 12; // Beschriftung über dem Feld (Grundlinie bei 8 mm wie im Kasten, Feld ab 12 mm)
export const MATCH_H = 7;
export const BOX_PAD = 5;
export const BOX_GAP = GAP; // zwischen Feld und Kasten
export const MIN_GAP = 2 * BASE; // kleinste Lücke vor der Fußzeile
export const MAX_MATCHES_SHOWN = 8;

/** Welche Kacheln es gibt, in fester Reihenfolge (nur belegte). */
export const tileKeys = (facts) => TILE_KEYS.filter((k) => (facts || []).some((f) => f.key === k));

/**
 * Kachelraster. Reihe 1: drei gleich breite Zellen (bei vier Kacheln stattdessen zwei auf den Spalten). Alle weiteren
 * Reihen: zwei Zellen auf den Spalten (58 / 42, Kanten fluchten mit Spielfeld | Video-Kasten). Bleibt eine Zelle übrig,
 * hat sie die Standardbreite (ein Drittel) und steht links — nie gestreckt, nie über die ganze Breite.
 * @returns {Array<Array<{ index: number, x: number, w: number }>>}  x relativ zum linken Rand (M)
 */
export function tileGrid(count) {
  const std = (CONTENT_W - GAP * (TILE_COLS - 1)) / TILE_COLS;
  const rows = [];
  let i = 0;
  if (count === 3 || count >= 5) {
    rows.push(Array.from({ length: 3 }, (_, c) => ({ index: c, x: c * (std + GAP), w: std })));
    i = 3;
  }
  while (i < count) {
    if (count - i >= 2) {
      rows.push([{ index: i, x: 0, w: LEFT_W }, { index: i + 1, x: LEFT_W + GAP, w: RIGHT_W }]);
      i += 2;
    } else {
      rows.push([{ index: i, x: 0, w: std }]);
      i += 1;
    }
  }
  return rows;
}

/** Höhe des Kachelblocks. */
export const tilesBlockHeight = (count) => {
  const rows = tileGrid(count).length;
  return rows ? rows * tileHeight(count) + (rows - 1) * TILE_GAP : 0;
};

/** Anteil der Seitenhöhe, den die Vorschau zeigt: bis unter die erste Kachelreihe (nicht mitten durch Kacheln). */
export function previewCrop(count, top = TILES_Y) {
  const bottom = count > 0 ? top + tileHeight(count) + GAP / 2 : HEAD_H + 14;
  return bottom / PAGE.H;
}

/* Transferhistorie (F13): ein Kasten über die volle Breite unter Spielfeld und Video-Kasten, nur bei transferierten
   Spielern mit Einträgen. Eine Zeile je Wechsel; was nicht passt, wird als "+ n earlier transfers" gezählt. */
export const XFER_ROW_H = 6.5;
export const XFER_MAX_ROWS = 8;
/** So viele Zeilen gelten als "genug": das Spielfeld wird nur so weit verkleinert, bis sie passen (oder es nicht mehr geht). */
export const XFER_TARGET_ROWS = 3;
/** Kleinste Lücke zwischen den Blöcken, wenn die Transferhistorie dabei ist (sonst MIN_GAP). */
export const XFER_MIN_GAP = BASE;
/** Höhe des Transfer-Kastens für n Zeilen (mit Zeile "+ n earlier transfers"). */
export const xferHeight = (rows, hidden) => BOX_PAD + 6 + rows * XFER_ROW_H + (hidden ? 5 : 0) + 3;
/** Kleinster sinnvoller Kasten: eine Zeile, und bei mehr Einträgen die Zählzeile. */
export const xferMinHeight = (total) => xferHeight(1, total > 1);
/** Kleinste Spielfeldbreite, wenn der Transfer-Kasten Platz braucht (die linke Kante bleibt auf der Spaltenlinie). */
export const MIN_PITCH_W = 44;

/** Höhe des Material-Kastens für n Matches (mit Highlight, mit "+ n more"). */
export const materialHeight = (hasHighlight, rows, hidden) =>
  BOX_PAD + 8 + (hasHighlight ? 16 : 0) + (rows ? 9 + rows * MATCH_H : 0) + (hidden ? 6 : 0) + BOX_PAD - 1;

/**
 * Anordnung der ganzen Seite aus dem Profil (nur Zahlen). Die freie Fläche wird auf die Lücken verteilt: Kopf → Kacheln
 * ist fest (HEAD_TO_TILES), die Lücken unter den Kacheln und vor der Fußzeile sind gleich groß, mindestens MIN_GAP.
 * Neben einem Video-Kasten steht das Spielfeld fest in der linken Spalte (58 %), der Kasten in der rechten (42 %);
 * ohne Kasten darf das Feld wachsen, solange die Lücken groß sind (bis zur ganzen Breite). Bei voller Karte werden
 * überzählige Matches gezählt statt gezeichnet.
 *
 * @param {{ facts: Array, links: Array, heat: Array, material: null|object }} profile
 */
export function layoutCard(profile) {
  const count = tileKeys(profile.facts).length;
  const tilesBlock = tilesBlockHeight(count);
  const hasTm = (profile.links || []).some((l) => l.key === 'transfermarkt');
  const tmH = hasTm ? 8 : 0;
  const topBottom = TILES_Y + tilesBlock + tmH; // für die Passrechnung; die echte Oberkante folgt unten

  const hasHeat = profile.heat.length > 0;
  const mat = profile.material;
  const matchCount = mat ? Math.min(mat.matches.length, MAX_MATCHES_SHOWN) : 0;
  const hasMatches = matchCount > 0;
  const highlightOnly = !!mat && !hasMatches;
  const hasHighlight = !!(mat && mat.highlight);

  /* so viele Matches, wie bei kleinsten Lücken Platz haben */
  /* Transferhistorie: ihr kleinster Kasten und eine Lücke werden vorab zurückgelegt, damit Matches nicht den ganzen Platz nehmen */
  const xferTotal = Array.isArray(profile.transfers) ? profile.transfers.length : 0;
  const gapMin = xferTotal ? XFER_MIN_GAP : MIN_GAP;
  /* zurückgelegt wird der Platz für XFER_TARGET_ROWS Zeilen: Matches weichen zuerst, dann das Spielfeld */
  const xferTarget = Math.min(xferTotal, XFER_TARGET_ROWS);
  const xferReserve = xferTotal ? xferHeight(xferTarget, xferTotal > xferTarget) + gapMin : 0;
  const avail = FOOT_LINE - topBottom - 2 * gapMin - xferReserve;
  let shown = matchCount;
  while (shown > 0 && materialHeight(hasHighlight, shown, (mat.more || 0) + (mat.matches.length - shown)) > avail) shown -= 1;
  const hidden = mat ? (mat.more || 0) + (mat.matches.length - shown) : 0;
  const matBox = hasMatches ? materialHeight(hasHighlight, shown, hidden) : 0;

  const heatBlock = (w) => (hasHeat ? w * HEAT_RATIO + HEAT_EXTRA : 0);
  const blockHeight = (w) => {
    if (hasHeat && mat) return Math.max(heatBlock(w), highlightOnly ? heatBlock(w) : matBox);
    if (hasHeat) return heatBlock(w);
    if (mat) return highlightOnly ? 70 : matBox;
    return 0;
  };

  let pitchW = 0;
  if (hasHeat && mat) {
    pitchW = LEFT_W;
  } else if (hasHeat) {
    pitchW = 90;
    const room = FOOT_LINE - topBottom - xferReserve;
    /* wachsen, solange die Lücken groß bleiben (mindestens 12 mm) */
    while (pitchW + 2 <= CONTENT_W && (room - blockHeight(pitchW + 2)) / 2 >= 12) pitchW += 2;
  }
  /* Zeilen des Transfer-Kastens, die bei der Spielfeldbreite w neben den anderen Blöcken Platz haben (kleinste Lücken) */
  const xferFit = (w) => {
    const room = FOOT_LINE - HEAD_H - tilesBlock - tmH - blockHeight(w) - HEAD_TO_TILES - (blockHeight(w) ? 3 : 2) * gapMin;
    let r = Math.min(xferTotal, XFER_MAX_ROWS);
    while (r > 0 && xferHeight(r, xferTotal - r) > room) r -= 1;
    return r;
  };
  /* Mit Transferhistorie: Spielfeld schmaler, bis XFER_TARGET_ROWS Zeilen passen oder MIN_PITCH_W erreicht ist (die linke
     Kante bleibt auf der Spaltenlinie, der Video-Kasten steht unverändert auf der rechten) */
  if (xferTotal && hasHeat) {
    const target = Math.min(xferTotal, XFER_TARGET_ROWS);
    while (pitchW - 2 >= MIN_PITCH_W && xferFit(pitchW) < target) pitchW -= 2;
  }

  const lowerH = blockHeight(pitchW || 0);
  /* Freie Fläche auf Kopf → Kacheln, Kacheln → Feld und Feld → Fußzeile verteilen: drei gleiche Lücken, sobald es
     genug ist (dünne Karten), sonst bleibt die Lücke unter dem Kopf bei 14 mm und die anderen teilen den Rest. */
  const free = FOOT_LINE - HEAD_H - tilesBlock - tmH - lowerH;
  let headGap = HEAD_TO_TILES;
  let gap = 0;
  /* Transfer-Kasten: so viele Zeilen (neueste zuerst), wie bei kleinsten Lücken Platz haben; der Kopfabstand bleibt fest,
     die übrige Fläche teilen sich gleiche Lücken. Ohne Einträge (xferTotal 0) läuft alles wie vor F13. */
  let xferRows = 0;
  let xferH = 0;
  const xferGaps = lowerH ? 3 : 2;
  if (xferTotal) {
    const room = free - HEAD_TO_TILES - xferGaps * gapMin;
    xferRows = Math.min(xferTotal, XFER_MAX_ROWS);
    while (xferRows > 0 && xferHeight(xferRows, xferTotal - xferRows) > room) xferRows -= 1;
    if (xferRows) {
      xferH = xferHeight(xferRows, xferTotal - xferRows);
      gap = (free - HEAD_TO_TILES - xferH) / xferGaps;
    }
  }
  if (!xferRows && lowerH) {
    if (free / 3 > HEAD_TO_TILES) {
      headGap = free / 3;
      gap = free / 3;
    } else {
      gap = Math.max(MIN_GAP, (free - HEAD_TO_TILES) / 2);
    }
  }
  const tiles = { count, rows: tileGrid(count), h: tileHeight(count), top: HEAD_H + headGap, block: tilesBlock };
  const lowTop = tiles.top + tilesBlock + tmH;
  const lower = {
    present: lowerH > 0,
    top: lowTop + gap,
    h: lowerH,
    pitchW,
    heat: hasHeat,
    material: !!mat,
    highlightOnly,
    shown,
    hidden,
    boxX: hasHeat ? COL_X[1] : PAGE.M,
    boxW: hasHeat ? RIGHT_W : CONTENT_W,
  };
  const gaps = { head: headGap, mid: lower.present || xferRows ? gap : 0, foot: lower.present || xferRows ? gap : 0 };
  const xferTop = (lower.present ? lower.top + lower.h : lowTop) + gap;
  const xfer = {
    present: xferRows > 0, top: xferTop, h: xferH, shown: xferRows, hidden: xferRows ? xferTotal - xferRows : 0, x: PAGE.M, w: CONTENT_W,
  };
  const bottom = xfer.present ? xfer.top + xfer.h : lower.present ? lower.top + lower.h : lowTop;
  return { tiles, tm: hasTm ? { y: tiles.top + tiles.block + 6 } : null, lower, xfer, gaps, bottom };
}
